import { useLocationService } from '@grafana/runtime';
import type { ChatContextItem } from './types';
import { useEffect, useState, useRef, useCallback } from 'react';

// Extend window interface for global registry
declare global {
  interface Window {
    [key: string]: any;
  }
}

export interface PageContextRegistration {
  id: string;
  urlPattern: string | RegExp;
  context: ChatContextItem[];
}

// Global registry key
const GLOBAL_REGISTRY_KEY = '__grafanaAssistantPageContextRegistry__';

// Custom event for registry changes
const REGISTRY_CHANGE_EVENT = 'grafana-assistant:page-context-registry-changed';

/**
 * Legacy event names for backward compatibility with the previous implementation.
 * These events are emitted alongside the new REGISTRY_CHANGE_EVENT to ensure
 * any existing code listening to the old events continues to work.
 *
 * @deprecated These event name is maintained for backward compatibility only.
 * TODO: Remove after old event-based approach is fully deprecated
 */
const LEGACY_REGISTRY_UPDATE_EVENT = 'grafana-assistant:page-context-update';
/**
 * @deprecated These event name is maintained for backward compatibility only.
 * TODO: Remove after old event-based approach is fully deprecated
 */
const LEGACY_REGISTRY_REMOVE_EVENT = 'grafana-assistant:page-context-remove';
/**
 * @deprecated These event name is maintained for backward compatibility only.
 * TODO: Remove after old event-based approach is fully deprecated
 */
const LEGACY_REGISTRY_SYNC_EVENT = 'grafana-assistant:page-context-sync';

// Ensure global registry exists on window
if (!window[GLOBAL_REGISTRY_KEY]) {
  window[GLOBAL_REGISTRY_KEY] = [];
}

// Get reference to the global registry
function getPageContextRegistry(): PageContextRegistration[] {
  return window[GLOBAL_REGISTRY_KEY];
}

// TODO: Remove skipLegacyEvents parameter after old event-based approach is fully deprecated
function setPageContextRegistry(newRegistry: PageContextRegistration[], skipLegacyEvents = false): void {
  window[GLOBAL_REGISTRY_KEY] = newRegistry;
  // Notify all listeners that the registry has changed
  window.dispatchEvent(new CustomEvent(REGISTRY_CHANGE_EVENT));

  // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
  // Emit legacy sync event for backward compatibility (unless we're processing a legacy event)
  if (!skipLegacyEvents) {
    window.dispatchEvent(
      new CustomEvent(LEGACY_REGISTRY_SYNC_EVENT, {
        detail: { registry: newRegistry },
      })
    );
  }
}

/**
 * Bridge to handle legacy events from external apps using old versions of @grafana/assistant.
 * This ensures that registrations from external apps get synced into our window registry.
 *
 * TODO: Remove this entire bridge after old event-based approach is fully deprecated
 */
let legacyBridgeInitialized = false;

function initializeLegacyEventBridge(): void {
  if (legacyBridgeInitialized) {
    return;
  }
  legacyBridgeInitialized = true;

  // Handle legacy update events (registration added or updated)
  window.addEventListener(LEGACY_REGISTRY_UPDATE_EVENT, (event: Event) => {
    const customEvent = event as CustomEvent;
    const registration = customEvent.detail as PageContextRegistration;

    if (!registration?.id) {
      return;
    }

    const currentRegistry = getPageContextRegistry();
    const existingIndex = currentRegistry.findIndex((r) => r.id === registration.id);

    if (existingIndex !== -1) {
      // Update existing registration
      const newRegistry = [...currentRegistry];
      newRegistry[existingIndex] = registration;
      setPageContextRegistry(newRegistry, true); // Skip legacy events to avoid loops
    } else {
      // Add new registration
      setPageContextRegistry([...currentRegistry, registration], true); // Skip legacy events to avoid loops
    }
  });

  // Handle legacy remove events (registration removed)
  window.addEventListener(LEGACY_REGISTRY_REMOVE_EVENT, (event: Event) => {
    const customEvent = event as CustomEvent;
    const id = customEvent.detail?.id;

    if (!id) {
      return;
    }

    const currentRegistry = getPageContextRegistry();
    const index = currentRegistry.findIndex((r) => r.id === id);

    if (index !== -1) {
      const newRegistry = currentRegistry.filter((_, i) => i !== index);
      setPageContextRegistry(newRegistry, true); // Skip legacy events to avoid loops
    }
  });

  // Handle legacy sync events (entire registry sync from external app)
  window.addEventListener(LEGACY_REGISTRY_SYNC_EVENT, (event: Event) => {
    const customEvent = event as CustomEvent;
    const incomingRegistry = customEvent.detail?.registry as PageContextRegistration[];

    if (!Array.isArray(incomingRegistry)) {
      return;
    }

    const currentRegistry = getPageContextRegistry();
    const currentIds = new Set(currentRegistry.map((r) => r.id));

    // Merge: add any registrations from external app that we don't have
    const newRegistrations = incomingRegistry.filter((r) => !currentIds.has(r.id));

    if (newRegistrations.length > 0) {
      setPageContextRegistry([...currentRegistry, ...newRegistrations], true); // Skip legacy events to avoid loops
    }
  });
}

// TODO: Remove this initialization after old event-based approach is fully deprecated
// Initialize the bridge on module load
initializeLegacyEventBridge();

/**
 * Registers context items for specific pages based on URL patterns.
 * Returns a setter function to update the context dynamically, similar to useState.
 *
 * Multiple registrations with the same URL pattern are allowed and will all be merged
 * when the pattern matches. Each call creates a new independent registration.
 *
 * @param urlPattern - URL pattern (string or RegExp) to match against page URLs
 * @param initialContext - *Initial* array of ContextItems to provide when the pattern matches. IMPORTANT: will be overwritten when setContext is used to update the context.
 * @returns A setter function to update the context, with an unregister method attached
 */
export function providePageContext(
  urlPattern: string | RegExp,
  initialContext: ChatContextItem[]
): ((context: ChatContextItem[]) => void) & { unregister: () => void } {
  // Validate URL pattern at registration time (only warn once, not on every match)
  validateUrlPattern(urlPattern);

  // Always create a new registration to allow multiple providers with the same URL pattern
  const registration: PageContextRegistration = {
    id: generateRegistrationId(),
    urlPattern,
    context: [...initialContext],
  };

  const registry = getPageContextRegistry();
  setPageContextRegistry([...registry, registration]);

  // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
  // Emit legacy update event for backward compatibility
  window.dispatchEvent(
    new CustomEvent(LEGACY_REGISTRY_UPDATE_EVENT, {
      detail: registration,
    })
  );

  // Create setter function that can update the context
  const setContext = (newContext: ChatContextItem[]) => {
    const currentRegistry = getPageContextRegistry();
    const index = currentRegistry.findIndex((reg) => reg.id === registration.id);
    if (index !== -1) {
      const newRegistry = [...currentRegistry];
      newRegistry[index] = { ...newRegistry[index], context: [...newContext] };
      setPageContextRegistry(newRegistry);

      // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
      // Emit legacy update event for backward compatibility
      window.dispatchEvent(
        new CustomEvent(LEGACY_REGISTRY_UPDATE_EVENT, {
          detail: newRegistry[index],
        })
      );
    }
  };

  // Add unregister method to the setter
  setContext.unregister = () => {
    const currentRegistry = getPageContextRegistry();
    const index = currentRegistry.findIndex((reg) => reg.id === registration.id);
    if (index !== -1) {
      const newRegistry = currentRegistry.filter((_, i) => i !== index);
      setPageContextRegistry(newRegistry);

      // TODO: Remove this legacy event emission after old event-based approach is fully deprecated
      // Emit legacy remove event for backward compatibility
      window.dispatchEvent(
        new CustomEvent(LEGACY_REGISTRY_REMOVE_EVENT, {
          detail: { id: registration.id },
        })
      );
    }
  };

  return setContext;
}

const empty: ChatContextItem[] = [];

/**
 * React hook for providing page context that automatically cleans up on unmount.
 * This is the recommended way to use page context in React components.
 *
 * @param urlPattern - URL pattern (string or RegExp) to match against page URLs
 * @param initialContext - Initial array of ContextItems to provide when the pattern matches. IMPORTANT: will be overwritten when setContext is used to update the context.
 * @returns A setter function to update the context
 */
export function useProvidePageContext(
  urlPattern: string | RegExp,
  initialContext: ChatContextItem[] = empty
): (context: ChatContextItem[]) => void {
  const setContextRef = useRef<ReturnType<typeof providePageContext> | undefined>(undefined);
  const pendingUpdate = useRef<ChatContextItem[] | undefined>(undefined);
  const isModified = useRef(false);

  // Create or update the registration when dependencies change
  // biome-ignore lint/correctness/useExhaustiveDependencies: initialContext is intentionally excluded to prevent re-registering on every render; updates are handled by the second effect
  useEffect(() => {
    // Reset isModified flag when urlPattern changes so that initialContext updates
    // are applied to the new registration until manually modified again
    isModified.current = false;

    // If we have pending updates, prefer those over initialContext as they represent
    // newer state from child components
    const contextToUse = pendingUpdate.current !== undefined ? pendingUpdate.current : initialContext;
    setContextRef.current = providePageContext(urlPattern, contextToUse);

    // Clear pending update as it's now applied
    if (pendingUpdate.current !== undefined) {
      pendingUpdate.current = undefined;
    }

    // Cleanup on unmount or dependency change
    return () => {
      setContextRef.current?.unregister();
      setContextRef.current = undefined;
    };
    // We intentionally exclude initialContext from deps to prevent re-registering on every render
    // Updates to initialContext are handled by the second effect
  }, [urlPattern]);

  // Update context when initialContext changes, BUT only if we haven't manually modified it.
  // This prevents unstable initialContext props (e.g. passing [] inline) from overwriting
  // updates made via setContext by child components.
  useEffect(() => {
    if (!isModified.current && setContextRef.current) {
      setContextRef.current(initialContext);
    }
  }, [initialContext]);

  // Return a stable setter function
  const setContext = useCallback((newContext: ChatContextItem[]) => {
    isModified.current = true;
    if (setContextRef.current) {
      setContextRef.current(newContext);
    } else {
      pendingUpdate.current = newContext;
    }
  }, []);

  return setContext;
}

/**
 * Gets all context items that match the current URL.
 *
 * @param currentUrl - The current page URL to match against
 * @param registrations - Array of registrations to match against
 * @returns Array of ChatContextItem from all matching registrations
 */
function getActivePageContext(currentUrl: string): ChatContextItem[] {
  if (!currentUrl) {
    return [];
  }

  const activeContext: ChatContextItem[] = [];
  const registry = getPageContextRegistry();

  for (const registration of registry) {
    if (matchesUrlPattern(currentUrl, registration.urlPattern)) {
      activeContext.push(...registration.context);
    }
  }

  return activeContext;
}

export function usePageContext(
  options: { allowQuestions: boolean; allowComponents: boolean } = { allowQuestions: false, allowComponents: false }
): ChatContextItem[] {
  const location = useLocationService();
  const initialPathname = location.getLocation().pathname;
  const [pageContext, setPageContext] = useState<ChatContextItem[]>(() => getActivePageContext(initialPathname));
  const currentPathnameRef = useRef(initialPathname);

  useEffect(() => {
    // Function to update context based on current location and registry
    const updateContext = () => {
      const pathname = location.getLocation().pathname;
      const context = getActivePageContext(pathname);
      setPageContext(context);
    };

    // Handle registry changes
    const handleRegistryChange = () => {
      updateContext();
    };

    // Subscribe to location changes
    const locationSub = location.getLocationObservable().subscribe((loc: { pathname: string }) => {
      const pathname = loc.pathname;
      if (pathname !== currentPathnameRef.current) {
        currentPathnameRef.current = pathname;
        updateContext();
      }
    });

    // Listen for registry changes
    window.addEventListener(REGISTRY_CHANGE_EVENT, handleRegistryChange);

    // TODO: Remove these legacy event listeners after old event-based approach is fully deprecated
    // Listen for legacy events for backward compatibility
    window.addEventListener(LEGACY_REGISTRY_UPDATE_EVENT, handleRegistryChange);
    window.addEventListener(LEGACY_REGISTRY_REMOVE_EVENT, handleRegistryChange);
    window.addEventListener(LEGACY_REGISTRY_SYNC_EVENT, handleRegistryChange);

    // Initial update
    updateContext();

    return () => {
      locationSub.unsubscribe();
      window.removeEventListener(REGISTRY_CHANGE_EVENT, handleRegistryChange);
      // TODO: Remove these legacy event listener removals after old event-based approach is fully deprecated
      window.removeEventListener(LEGACY_REGISTRY_UPDATE_EVENT, handleRegistryChange);
      window.removeEventListener(LEGACY_REGISTRY_REMOVE_EVENT, handleRegistryChange);
      window.removeEventListener(LEGACY_REGISTRY_SYNC_EVENT, handleRegistryChange);
    };
  }, [location]);

  let filteredContext = pageContext;

  if (!options.allowQuestions) {
    filteredContext = filteredContext.filter((item) => item.node.data?.type !== 'question');
  }

  if (!options.allowComponents) {
    filteredContext = filteredContext.filter((item) => item.node.data?.type !== 'component');
  }

  return filteredContext;
}

export function generateRegistrationId(prefix = 'page-context'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Validates the URL pattern at registration time and logs warnings for problematic patterns.
 * This is called once during registration, not on every URL match.
 */
function validateUrlPattern(pattern: string | RegExp): void {
  if (pattern instanceof RegExp) {
    // Check if RegExp pattern matches everything (e.g., /.*/, /^.*$/)
    // This is intentional for global contexts (e.g., components), so we use a scoped warning
    const source = pattern.source;
    if (source === '.*' || source === '^.*$' || source === '^.*' || source === '.*$') {
      // Note: This is expected for global contexts like provideComponents.
      // The warning is intentionally omitted here since it's a valid use case.
      // If you want to warn for specific cases, filter them here.
    }
    return;
  }

  if (typeof pattern === 'string') {
    // Validate urlPattern - log error if empty string is passed
    if (!pattern || pattern.trim() === '') {
      console.warn(
        'useProvidePageContext: Empty string passed as urlPattern. This will cause the context to not match any pages.'
      );
      return;
    }

    // Check if the pattern matches everything (e.g., ** becomes ^.*$)
    if (pattern === '**') {
      // Note: This is expected for global contexts.
      // The warning is intentionally omitted here since it's a valid use case.
    }
  }
}

function matchesUrlPattern(url: string, pattern: string | RegExp): boolean {
  if (pattern instanceof RegExp) {
    return pattern.test(url);
  }

  // For string patterns, support simple wildcards
  if (typeof pattern === 'string') {
    // Empty pattern never matches
    if (!pattern || pattern.trim() === '') {
      return false;
    }

    // Convert simple wildcard pattern to regex
    // * matches any characters except /, ** matches any path segments
    // Handle ** first with a placeholder to avoid conflicts
    const regexPattern = pattern
      .replace(/\*\*/g, '\u0000DOUBLE_STAR\u0000') // Temporary placeholder for **
      .replace(/\*/g, '[^/]*') // * becomes [^/]*
      // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional \u0000 null byte used as placeholder to avoid glob pattern conflicts
      .replace(/\u0000DOUBLE_STAR\u0000/g, '.*') // Replace placeholder with .*
      .replace(/\?/g, '.'); // ? becomes .

    const finalRegex = `^${regexPattern}$`;
    const regex = new RegExp(finalRegex);
    return regex.test(url);
  }

  return false;
}

/**
 * Test utility function to clear all registrations from the registry.
 * This should only be used in tests to ensure a clean slate between test runs.
 *
 * @internal This is for testing purposes only
 */
export function __clearPageContextRegistryForTesting(): void {
  setPageContextRegistry([]);
}
